import { type ReactNode } from 'react'
import cx from 'classnames'
import { NumericSpinnerInput } from '@/components/inputs/NumericSpinnerInput'
import styles from './BundleDialog.module.css'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
  ModalSheetSection,
} from '@/components/ModalSheet'
import { FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import { Button } from '@/components/Button'

import { ArchivedItemsIndicator } from '@/features/admin/bundle/components/ArchivedItemsIndicator'
import { match } from '@/utils/pattern-match-util'
import { type BundleDialogVariant } from './BundleDialogViewModel'

export interface BundleDialogProps {
  variant: BundleDialogVariant
  showing: boolean
  onClose: () => unknown
  isSubmitPending: boolean
  /**
   * The id of the bundle form the footer's submit button submits.
   */
  formId: string
  /**
   * When set, the footer warns that the bundle contains this many archived items.
   */
  archivedItemsCount?: number
  /**
   * The bundle form.
   */
  children?: ReactNode
  manageItemsDialog?: ReactNode
  closeButtonTestId?: string
  submitButtonTestId?: string
}

export function BundleDialog({
  variant,
  showing,
  onClose,
  isSubmitPending,
  formId,
  archivedItemsCount,
  children,
  manageItemsDialog,
  closeButtonTestId,
  submitButtonTestId,
}: BundleDialogProps) {
  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>
          {match(variant, {
            Create: () => 'Add bundle',
            Edit: () => 'Edit bundle',
            Duplicate: () => 'Duplicate bundle',
          })}
        </ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetSection spaced>{children}</ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        {archivedItemsCount !== undefined && (
          <div className={styles.editBundleFooterIndicator}>
            <ArchivedItemsIndicator count={archivedItemsCount} />
          </div>
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            testId={closeButtonTestId}
            isDisabled={isSubmitPending}
            onPress={onClose}
            variant="default"
          />

          <Button
            label={match(variant, {
              Create: () => 'Add new',
              Edit: () => 'Save',
              Duplicate: () => 'Save',
            })}
            type="submit"
            form={formId}
            variant="primary"
            isPending={isSubmitPending}
            isDisabled={isSubmitPending}
            testId={submitButtonTestId}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
      {manageItemsDialog}
    </ModalSheet>
  )
}

export interface BundleIncludedItemsProps {
  includedItemsCount: number
  onManageItems: () => unknown
  isManageItemsDisabled: boolean
  children?: ReactNode
  manageItemsButtonTestId?: string
}

export function BundleIncludedItems({
  includedItemsCount,
  onManageItems,
  isManageItemsDisabled,
  children,
  manageItemsButtonTestId,
}: BundleIncludedItemsProps) {
  const isRelevantIncludedItemViewModelsEmpty = includedItemsCount === 0

  return (
    <FormRow>
      <FormGroupContainer>
        <label className={styles.includedItemsLabel}>
          <div>
            Service items
            <span className={styles.includedItemCount}>
              ({includedItemsCount || 0})
            </span>
          </div>
          {!isRelevantIncludedItemViewModelsEmpty && (
            <div className={styles.includedItemQuantityLabel}>
              Included quantity
            </div>
          )}
        </label>
        <div>
          {isRelevantIncludedItemViewModelsEmpty && <NoIncludedItems />}
          {children}
        </div>

        <Button
          label="Manage service items"
          onPress={onManageItems}
          className={styles.includedItemRowManageButton}
          variant="text"
          size="normal"
          isDisabled={isManageItemsDisabled}
          testId={manageItemsButtonTestId}
        />
      </FormGroupContainer>
    </FormRow>
  )
}

export interface BundleIncludedItemProps {
  variant: BundleDialogVariant
  name: string
  description: string
  isBaseItem: boolean
  isIncludedItem: boolean
  archived: boolean
  serviceItemStatusMessage: string
  includedQuantity: number
  onQuantityChange: (value: number | null) => unknown
  quantityInputTestId?: string
}

export function BundleIncludedItem({
  variant,
  name,
  description,
  isBaseItem,
  isIncludedItem,
  archived,
  serviceItemStatusMessage,
  includedQuantity,
  onQuantityChange,
  quantityInputTestId,
}: BundleIncludedItemProps) {
  const isQuantityChangeDisabled = !isIncludedItem || isBaseItem
  const isEdit = variant === 'Edit'
  return (
    <div
      className={cx(
        styles.includedItem,
        isEdit && archived && styles.archivedItemContainer,
      )}
    >
      <div className={styles.includedItemInfo}>
        <div className={styles.itemHeader}>
          <span>{name}</span>
          {isEdit
            ? (archived || isBaseItem) && (
                <span className={styles.serviceItemIndicator}>
                  {serviceItemStatusMessage}
                </span>
              )
            : isBaseItem && (
                <span className={styles.baseItem}>* base item</span>
              )}
        </div>
        <div className={styles.includedDescription}>{description}</div>
      </div>
      <div className={styles.itemQuantityContainer}>
        <NumericSpinnerInput
          className={cx(
            styles.itemQuantityBox,
            isQuantityChangeDisabled && styles.isDisabled,
          )}
          min={0}
          value={includedQuantity}
          onChange={onQuantityChange}
          isDisabled={isQuantityChangeDisabled}
          testId={quantityInputTestId}
        />
      </div>
    </div>
  )
}

function NoIncludedItems() {
  return (
    <div className={cx(styles.includedItem, styles.noneAssigned)}>
      None assigned
    </div>
  )
}
